import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, canEdit, type AuthUser } from '@/lib/api';
import { usePlanRegistryVersion } from '@/lib/usePlanVariants';
import {
  DEFAULT_CS_VARIANT,
  DEFAULT_DS_VARIANT,
  extractDSEditable,
  extractCSEditable,
  extractDawnbreakersEditable,
  resolveCSVariant,
  resolveDSVariant,
  getPlanDefault,
  getCustomPlanRegistry,
  OWN_NON_VIRUS_SLOTS,
  OWN_SLOT_KEYS,
  DS_NON_SILO_SLOTS,
  NON_VIRUS_SLOTS,
  type CustomVariantEntry,
} from '@/plans';
import type { DSSlotKey, DSNonSiloSlot } from '@/plans';
import type { PlanSlotKey, NonVirusSlot } from '@/plans';
import type { OwnSideSlotKey, OwnSideNonVirusSlot } from '@/plans';

// ---- Slot labels ----

const DS_LABEL: Record<DSSlotKey, string> = {
  infoCenter: 'Info Center',
  arsenal: 'Arsenal',
  mercenary: 'Mercenary',
  nuclearSilo: 'Nuclear Silo',
  scienceHub: 'Science Hub',
  fieldHospital1: 'Field Hospital I',
  fieldHospital2: 'Field Hospital II',
  fieldHospital3: 'Field Hospital III',
  fieldHospital4: 'Field Hospital IV',
  oilRefinery1: 'Oil Refinery I',
  oilRefinery2: 'Oil Refinery II',
};

const CS_LABEL: Record<PlanSlotKey, string> = {
  dataCenter1: 'Data Center I',
  dataCenter2: 'Data Center II',
  powerTower: 'Power Tower',
  serumFactory1: 'Serum Factory I',
  serumFactory2: 'Serum Factory II',
  defenseSystem1: 'Defense System I',
  defenseSystem2: 'Defense System II',
  virusLab: 'Virus Lab',
  warehouse1: 'Warehouse I',
  warehouse2: 'Warehouse II',
  warehouse3: 'Warehouse III',
  warehouse4: 'Warehouse IV',
};

const OWN_LABEL: Record<OwnSideSlotKey, string> = {
  dataCenterOwn: 'Data Center (own)',
  defenseSystemOwn: 'Defense System (own)',
  serumFactoryOwn: 'Serum Factory (own)',
  warehouseOwnA: 'Warehouse A (own)',
  warehouseOwnB: 'Warehouse B (own)',
  powerTower: 'Power Tower',
  virusLab: 'Virus Lab',
};

// ---- Types for the editor ----

type Family = 'ds' | 'cs' | 'dawnbreakers';

type Editable = {
  phases: Array<{
    capacities: Record<string, number>;
    strengthClaims?: Array<{ slot: string; count: number }>;
  }>;
  homeByRank: string[];
  coreRanks?: number[];   // DS
  ptCoreRanks?: number[]; // CS + Dawnbreakers
};

function familyOf(kind: 'ds' | 'cs', basedOn: string): Family | null {
  if (kind === 'ds') return 'ds';
  if (basedOn === 'dawnbreakers') return 'dawnbreakers';
  if (basedOn === 'rulebringers') return 'cs';
  return null;
}

function labelsFor(fam: Family): Record<string, string> {
  if (fam === 'ds') return DS_LABEL;
  if (fam === 'cs') return CS_LABEL;
  return OWN_LABEL;
}

function nonSpecialSlotsFor(fam: Family): readonly string[] {
  if (fam === 'ds') return DS_NON_SILO_SLOTS;
  if (fam === 'cs') return NON_VIRUS_SLOTS;
  return OWN_NON_VIRUS_SLOTS;
}

function allSlotsFor(fam: Family): readonly string[] {
  if (fam === 'ds') return Object.keys(DS_LABEL);
  if (fam === 'cs') return Object.keys(CS_LABEL);
  return OWN_SLOT_KEYS;
}

function specialLabel(fam: Family): string {
  if (fam === 'ds') return 'Silo core ranks';
  return 'PT core ranks';
}

function loadSeedEditable(
  kind: 'ds' | 'cs',
  basedOn: string,
  fam: Family,
): Editable {
  if (kind === 'ds') {
    const cfg = resolveDSVariant(basedOn);
    return extractDSEditable(cfg) as unknown as Editable;
  }
  if (fam === 'dawnbreakers') {
    return extractDawnbreakersEditable() as unknown as Editable;
  }
  const cfg = resolveCSVariant(basedOn);
  return extractCSEditable(cfg) as unknown as Editable;
}

// ---- Component ----

type Mode = 'edit' | 'saveAs';

export function PlanVariantEditor({
  open,
  onClose,
  kind,
  user,
  initialVariantKey,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  kind: 'ds' | 'cs';
  user: AuthUser;
  initialVariantKey: string | null;
  onSaved: (key: string) => void;
}) {
  const qc = useQueryClient();
  const registryVersion = usePlanRegistryVersion();
  void registryVersion; // triggers re-render on hydration
  const registry = getCustomPlanRegistry()[kind];
  const startingVariant: CustomVariantEntry | null = useMemo(() => {
    if (!initialVariantKey) return null;
    return registry.find((v) => v.key === initialVariantKey) ?? null;
  }, [initialVariantKey, registry]);

  const startingBasedOn =
    startingVariant?.basedOn ??
    initialVariantKey ??
    (kind === 'ds' ? DEFAULT_DS_VARIANT : DEFAULT_CS_VARIANT);
  const fam = familyOf(kind, startingBasedOn) ?? 'ds';

  const [mode, setMode] = useState<Mode>(startingVariant ? 'edit' : 'saveAs');
  const [name, setName] = useState<string>(startingVariant?.name ?? '');
  const [editable, setEditable] = useState<Editable>(() => {
    if (startingVariant) return structuredClone(startingVariant.config as Editable);
    return loadSeedEditable(kind, startingBasedOn, fam);
  });

  useEffect(() => {
    if (!open) return;
    setMode(startingVariant ? 'edit' : 'saveAs');
    setName(startingVariant?.name ?? '');
    setEditable(
      startingVariant
        ? structuredClone(startingVariant.config as Editable)
        : loadSeedEditable(kind, startingBasedOn, fam),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialVariantKey]);

  const labels = labelsFor(fam);
  const nonSpecial = nonSpecialSlotsFor(fam);
  const allSlots = allSlotsFor(fam);

  const createMut = useMutation({
    mutationFn: (body: { name: string; config: Editable }) =>
      api.createPlanVariant({ kind, name: body.name, basedOn: startingBasedOn, config: body.config }),
    onSuccess: (dto) => {
      qc.invalidateQueries({ queryKey: ['plan-variants'] });
      onSaved(dto.key);
      onClose();
    },
  });

  const updateMut = useMutation({
    mutationFn: (body: { id: number; name?: string; config?: Editable }) =>
      api.updatePlanVariant(body.id, { name: body.name, config: body.config }),
    onSuccess: (dto) => {
      qc.invalidateQueries({ queryKey: ['plan-variants'] });
      onSaved(dto.key);
      onClose();
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => api.deletePlanVariant(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['plan-variants'] });
      // Fall back to seed default after deletion.
      onSaved(kind === 'ds' ? DEFAULT_DS_VARIANT : DEFAULT_CS_VARIANT);
      onClose();
    },
  });

  // Look up id from the cached list (our local registry is keyed by name only).
  const cached = qc.getQueryData<{ variants: Array<{ id: number; key: string; kind: string }> }>([
    'plan-variants',
  ]);
  const currentId =
    startingVariant
      ? cached?.variants.find((v) => v.kind === kind && v.key === startingVariant.key)?.id ?? null
      : null;

  const canEditRole = canEdit(user.role);

  if (!open) return null;

  const setCapacity = (phaseIdx: number, slot: string, value: number) => {
    setEditable((prev) => {
      const next = structuredClone(prev);
      next.phases[phaseIdx].capacities[slot] = Math.max(0, Math.min(30, value | 0));
      return next;
    });
  };

  const setHomeAt = (rank: number, slot: string) => {
    setEditable((prev) => {
      const next = structuredClone(prev);
      next.homeByRank[rank] = slot;
      return next;
    });
  };

  const setClaims = (
    phaseIdx: number,
    claims: Array<{ slot: string; count: number }>,
  ) => {
    setEditable((prev) => {
      const next = structuredClone(prev);
      next.phases[phaseIdx].strengthClaims = claims;
      return next;
    });
  };

  const setSpecialRanks = (ranks: number[]) => {
    setEditable((prev) => {
      const next = structuredClone(prev);
      if (kind === 'ds') next.coreRanks = ranks;
      else next.ptCoreRanks = ranks;
      return next;
    });
  };

  const specialRanks = (kind === 'ds' ? editable.coreRanks : editable.ptCoreRanks) ?? [];

  const resetToSeed = () => {
    setEditable(loadSeedEditable(kind, startingBasedOn, fam));
  };

  const trimmedName = name.trim();

  const doSave = () => {
    if (mode === 'saveAs') {
      if (!trimmedName) return;
      createMut.mutate({ name: trimmedName, config: editable });
    } else if (currentId != null) {
      updateMut.mutate({
        id: currentId,
        name: trimmedName || undefined,
        config: editable,
      });
    }
  };

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900/60 p-4 text-sm text-slate-100">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">
              {mode === 'saveAs' ? 'Save plan variant as…' : 'Edit plan variant'}
            </h2>
            <p className="text-xs text-slate-400">
              Based on <span className="font-mono">{startingBasedOn}</span>
              {' · '}
              Kind: {kind.toUpperCase()}
              {fam === 'dawnbreakers' && (
                <>
                  {' · '}
                  <span className="text-orange-300">Side-agnostic slots (own)</span>
                </>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs hover:bg-slate-700"
          >
            Close
          </button>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-2">
          <label className="text-xs text-slate-400">Name</label>
          <input
            className="flex-1 rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={mode === 'saveAs' ? 'e.g. Desertstorm — Hybrid' : undefined}
          />
          {startingVariant && (
            <button
              onClick={() => setMode(mode === 'edit' ? 'saveAs' : 'edit')}
              className="rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs hover:bg-slate-700"
            >
              {mode === 'edit' ? 'Switch to Save as…' : 'Switch to Save'}
            </button>
          )}
        </div>

        <div className="space-y-4">
          {/* Phases */}
          {editable.phases.map((phase, i) => {
            const phaseTotal = Object.values(phase.capacities).reduce(
              (a, b) => a + (Number(b) || 0),
              0,
            );
            return (
            <div key={i} className="rounded border border-slate-800 p-3">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-widest text-orange-400">
                  Phase {i + 1}
                </div>
                <div
                  className="rounded bg-slate-800 px-1.5 py-0.5 text-[11px] font-semibold text-slate-200"
                  title="Sum of capacities across all slots in this phase"
                >
                  {phaseTotal} players
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 md:grid-cols-3">
                {allSlots.map((slot) => (
                  <div key={slot} className="flex items-center justify-between gap-2">
                    <span className="truncate text-xs text-slate-300">{labels[slot]}</span>
                    <div className="flex items-center gap-1">
                      <button
                        className="h-6 w-6 rounded border border-slate-700 bg-slate-800 text-xs hover:bg-slate-700"
                        onClick={() =>
                          setCapacity(i, slot, (phase.capacities[slot] ?? 0) - 1)
                        }
                      >
                        −
                      </button>
                      <input
                        type="number"
                        min={0}
                        max={30}
                        className="h-6 w-12 rounded border border-slate-700 bg-slate-950 px-1 text-center text-xs"
                        value={phase.capacities[slot] ?? 0}
                        onChange={(e) => setCapacity(i, slot, Number(e.target.value))}
                      />
                      <button
                        className="h-6 w-6 rounded border border-slate-700 bg-slate-800 text-xs hover:bg-slate-700"
                        onClick={() =>
                          setCapacity(i, slot, (phase.capacities[slot] ?? 0) + 1)
                        }
                      >
                        +
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3">
                <ClaimsEditor
                  claims={phase.strengthClaims ?? []}
                  onChange={(c) => setClaims(i, c)}
                  allSlots={allSlots}
                  labels={labels}
                />
              </div>
            </div>
            );
          })}

          {/* Home by rank */}
          <div className="rounded border border-slate-800 p-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-orange-400">
              Home by rank
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 md:grid-cols-3 lg:grid-cols-4">
              {editable.homeByRank.map((slot, rank) => (
                <div key={rank} className="flex items-center gap-2">
                  <span className="w-8 text-right text-xs text-slate-500">#{rank}</span>
                  <select
                    className="flex-1 rounded border border-slate-700 bg-slate-950 px-1 py-0.5 text-xs"
                    value={slot}
                    onChange={(e) => setHomeAt(rank, e.target.value)}
                  >
                    {nonSpecial.map((s) => (
                      <option key={s} value={s}>
                        {labels[s]}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          {/* Special ranks */}
          <div className="rounded border border-slate-800 p-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-orange-400">
              {specialLabel(fam)}
            </div>
            <input
              className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 text-xs"
              value={specialRanks.join(', ')}
              onChange={(e) =>
                setSpecialRanks(
                  e.target.value
                    .split(',')
                    .map((s) => Number(s.trim()))
                    .filter((n) => Number.isInteger(n) && n >= 0),
                )
              }
              placeholder="e.g. 0, 1"
            />
            <div className="mt-1 text-[11px] text-slate-500">
              Comma-separated 0-indexed ranks that stage on the special tile and never
              receive a substitution.
            </div>
          </div>
        </div>

        {(createMut.error || updateMut.error || deleteMut.error) && (
          <div className="mt-3 rounded border border-rose-500/40 bg-rose-950/40 p-2 text-xs text-rose-200">
            {String(
              (createMut.error as Error)?.message ??
                (updateMut.error as Error)?.message ??
                (deleteMut.error as Error)?.message,
            )}
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-slate-800 pt-3">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={resetToSeed}
              className="rounded border border-slate-700 bg-slate-800 px-3 py-1 text-xs hover:bg-slate-700"
            >
              Reset to seed ({startingBasedOn})
            </button>
            {mode === 'edit' && currentId != null && (
              <button
                onClick={() => {
                  if (!confirm(`Delete variant "${startingVariant?.name}"?`)) return;
                  deleteMut.mutate(currentId);
                }}
                className="rounded border border-rose-700 bg-rose-950/40 px-3 py-1 text-xs text-rose-200 hover:bg-rose-900/40"
                disabled={deleteMut.isPending}
              >
                Delete
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="rounded border border-slate-700 bg-slate-800 px-3 py-1 text-xs hover:bg-slate-700"
            >
              Cancel
            </button>
            <button
              onClick={doSave}
              disabled={
                !canEditRole ||
                createMut.isPending ||
                updateMut.isPending ||
                (mode === 'saveAs' && !trimmedName)
              }
              className="rounded border border-orange-600 bg-orange-600 px-3 py-1 text-xs font-semibold text-white hover:bg-orange-500 disabled:opacity-50"
            >
              {mode === 'saveAs' ? 'Save as new' : 'Save'}
            </button>
          </div>
        </div>
    </div>
  );
}

function ClaimsEditor({
  claims,
  onChange,
  allSlots,
  labels,
}: {
  claims: Array<{ slot: string; count: number }>;
  onChange: (next: Array<{ slot: string; count: number }>) => void;
  allSlots: readonly string[];
  labels: Record<string, string>;
}) {
  return (
    <div>
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
        Strength claims (top-M1 → slot)
      </div>
      <div className="space-y-1">
        {claims.map((c, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <select
              value={c.slot}
              onChange={(e) =>
                onChange(
                  claims.map((x, i) => (i === idx ? { ...x, slot: e.target.value } : x)),
                )
              }
              className="rounded border border-slate-700 bg-slate-950 px-1 py-0.5 text-xs"
            >
              {allSlots.map((s) => (
                <option key={s} value={s}>
                  {labels[s]}
                </option>
              ))}
            </select>
            <input
              type="number"
              min={0}
              max={30}
              className="h-6 w-16 rounded border border-slate-700 bg-slate-950 px-1 text-center text-xs"
              value={c.count}
              onChange={(e) =>
                onChange(
                  claims.map((x, i) =>
                    i === idx ? { ...x, count: Number(e.target.value) } : x,
                  ),
                )
              }
            />
            <button
              onClick={() => onChange(claims.filter((_, i) => i !== idx))}
              className="rounded border border-slate-700 bg-slate-800 px-1.5 text-xs hover:bg-slate-700"
            >
              ×
            </button>
          </div>
        ))}
        <button
          onClick={() =>
            onChange([...claims, { slot: allSlots[0], count: 1 }])
          }
          className="rounded border border-slate-700 bg-slate-800 px-2 py-0.5 text-xs hover:bg-slate-700"
        >
          + add claim
        </button>
      </div>
    </div>
  );
}

// ---- Variant picker ----

export function PlanVariantPicker({
  kind,
  currentKey,
  onChange,
  user,
  onEdit,
  showAdminActions = true,
}: {
  kind: 'ds' | 'cs';
  currentKey: string | null;
  onChange: (key: string) => void;
  user: AuthUser | null;
  onEdit: (variantKey: string | null) => void;
  showAdminActions?: boolean;
}) {
  const qc = useQueryClient();
  const registryVersion = usePlanRegistryVersion();
  void registryVersion;
  const registry = getCustomPlanRegistry()[kind];
  const seedKeys =
    kind === 'ds'
      ? [
          { key: 'desertstorm', name: 'Desertstorm' },
          { key: 'desertstorm-one', name: 'Desertstorm (single map)' },
        ]
      : [
          { key: 'rulebringers', name: 'Rulebringers' },
          { key: 'dawnbreakers', name: 'Dawnbreakers' },
        ];
  const effectiveKey = currentKey ?? getPlanDefault(kind);
  const defaultKey = getPlanDefault(kind);
  const canEditRole = user ? canEdit(user.role) : false;
  const isCustom = registry.some((v) => v.key === effectiveKey);

  const setDefaultMut = useMutation({
    mutationFn: (variantKey: string) => api.setPlanDefault(kind, variantKey),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['plan-variants'] }),
  });

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <label className="text-slate-400">Variant</label>
      <select
        className="rounded border border-slate-700 bg-slate-950 px-2 py-1"
        value={effectiveKey}
        onChange={(e) => onChange(e.target.value)}
      >
        <optgroup label="Built-in">
          {seedKeys.map((s) => (
            <option key={s.key} value={s.key}>
              {s.name}
              {s.key === defaultKey ? ' ★' : ''}
            </option>
          ))}
        </optgroup>
        {registry.length > 0 && (
          <optgroup label="Custom">
            {registry.map((v) => (
              <option key={v.key} value={v.key}>
                {v.name}
                {v.key === defaultKey ? ' ★' : ''}
              </option>
            ))}
          </optgroup>
        )}
      </select>
      {showAdminActions && canEditRole && (
        <>
          {isCustom ? (
            <button
              onClick={() => onEdit(effectiveKey)}
              className="rounded border border-slate-700 bg-slate-800 px-2 py-1 hover:bg-slate-700"
            >
              Edit
            </button>
          ) : (
            <button
              onClick={() => onEdit(effectiveKey)}
              className="rounded border border-slate-700 bg-slate-800 px-2 py-1 hover:bg-slate-700"
            >
              Save as…
            </button>
          )}
          {effectiveKey !== defaultKey && (
            <button
              onClick={() => setDefaultMut.mutate(effectiveKey)}
              disabled={setDefaultMut.isPending}
              className="rounded border border-slate-700 bg-slate-800 px-2 py-1 hover:bg-slate-700"
            >
              Set as default
            </button>
          )}
        </>
      )}
    </div>
  );
}

// Convenience: expose the type helpers so consumers don't need direct imports.
export type { OwnSideNonVirusSlot, DSNonSiloSlot, NonVirusSlot };
